import { ContentCopyOutlined, DeleteOutline } from "@mui/icons-material";
import { Box, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import IntlMessages from "../../components/IntlMessages/IntlMessages";
import Schema from "../../core/models/Schema";
import { GRAY_60 } from "../../themes/colors";
import { useIntl } from "react-intl";
import { useState } from "react";

type SchemaDetailsProps = {
  schema: Schema;
  showDelete?: boolean;
  showSchemaId?: boolean;
  onDelete?(): void;
};

const SchemaDetails = ({
  schema,
  showDelete = false,
  showSchemaId = false,
  onDelete,
}: SchemaDetailsProps) => {
  const intl = useIntl();
  const [openTooltip, setOpenTooltip] = useState<boolean>(false);

  const copyTooltip = intl.formatMessage(
    { id: "copyClipboard" },
    { text: schema.schemaId }
  );

  const onCopy = () => {
    navigator.clipboard.writeText(schema.schemaId);
    setOpenTooltip(true);
    setTimeout(() => setOpenTooltip(false), 2000);
  };

  return (
    <Box data-testid="schemaDetails" className="space-y-4">
      {/* Schema */}
      <Box className="flex justify-between items-center">
        <Box className="space-y-3">
          <Typography variant="h6" className="opacity-50">
            {schema.createdBy}
          </Typography>
          <Typography variant="h6" fontWeight={600}>
            {schema.name}
          </Typography>
          <Box className="space-y-2">
            {showSchemaId && (
              <Box className="flex items-center space-x-[0.625rem]">
                <Typography variant="caption" component="div" fontSize="1rem">
                  {schema.schemaId}
                </Typography>
                <Tooltip title={copyTooltip} open={openTooltip}>
                  <IconButton
                    data-testid="schemaDetails.schemaId.copy"
                    className="p-0"
                    onClick={onCopy}
                  >
                    <ContentCopyOutlined color="primary" fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <Typography variant="caption" component="div" fontSize="1rem">
              <IntlMessages
                id="credentialDefinition.schema.version"
                values={{ version: schema.version }}
              />
            </Typography>
          </Box>
        </Box>
        {showDelete && (
          <IconButton data-testid="schemaDetails.delete" onClick={onDelete}>
            <DeleteOutline color="secondary" />
          </IconButton>
        )}
      </Box>
      <Divider />
      {/* Schema attributes */}
      <Box data-testid="schemaDetails.attributes" className="space-y-4">
        {schema.attributes.map((attr, index) => (
          <Box key={index} className="flex items-center">
            <Typography fontWeight={500}>{attr.name}</Typography>
            <Typography whiteSpace="pre-wrap" color={GRAY_60}>
              {" / "}
              <IntlMessages id={`schema.attribute.type.${attr.type}`} />
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export { SchemaDetails };
